import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import {
  createEditorSession
} from "actions/editor";

import _ from "lodash";
import cx from "classnames";
import { toast } from "react-toastify";

import { SuccessToast, FailureToast } from "components/Toast";
import { Editor } from "components/Editor";
import { Loader } from "components/ApplicationLoader";
import { PageHeader, HeaderContext } from "../pages/components/header";
import { Button } from "react-bootstrap";

import { PaletteComponentHeader } from "./header";
import { fetchIntegrations } from "actions/integrations/integrations";
import { fetchPaletteComponent, invalidatePaletteComponent, publishPaletteComponent, renamePaletteComponent, updatePaletteComponent } from "actions/brandKit/components";
import { PAGE, SLIDE } from "constants/widgetTypes";
import { PAGE_WIDGET } from "constants/brandKit/component";
import styles from "./paletteComponent.module.scss";
import { getPageViewedEventPayload } from "services/mixpanelUtil";
import { MIXPANEL_BRANDKIT_PUBLISHED, MIXPANEL_PAGE_VIEWED, MIXPANEL_PAGE_VIEWED_EDITOR } from "constants/mixpanelEvents";
import { createMixpanelEvent } from "actions/mixpanel";


const PaletteComponentNameField = ({ paletteComponent, onSave }) => {
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState("");

  const updateState = useSelector((state) => _.get(state, `brandKit.components.paletteComponent.update`));
  const updating = _.get(updateState, 'updating');
  const updated = _.get(updateState, 'updated');

  useEffect(() => {
    if(edit && !updating && updated){
      setEdit(false);
    }
  }, [updating, updated]);

  useEffect(() => {
    if(paletteComponent){
      setName(paletteComponent.name);
    }
  }, [_.get(paletteComponent, 'name')]);

  const handleNameChange = (e) => {
    setName(e.target.value);
  }

  const handleSave = () => {
    if(onSave){
      onSave(name);
    }
  }

  return (
    <div className={styles.nameField}>
      <span className={styles.label}>Palette Component:</span>
      <div className={styles.inputField}>
        {edit &&
          <>
            <input type="text" value={name} onChange={handleNameChange} />
            <div className={styles.actionGroup}>
              <button className={styles.action} disabled={updating} onClick={handleSave}>
                <i className="cleverstory-tick" />
              </button>
              <button className={styles.action} disabled={updating} onClick={() => setEdit(false)}>
                <i className="cleverstory-close" />
              </button>
            </div>
          </>
        }

        {!edit &&
          <>
            <div className={styles.name}>
              {_.get(paletteComponent, "name")}
            </div>
            <div className={styles.actionGroup}>
              <button className={styles.action} disabled={updating} onClick={() => setEdit(true)}>
                <i className="cleverstory-edit" />
              </button>
            </div>
          </>
        }
      </div>
    </div>
  );
}

export default function PaletteComponentEditorPage(props) {
  const dispatch = useDispatch();

  const { id } = useParams();

  const [headerContext, setHeaderContext] = useState({
    type: 'palette_component',
    entity: {},
    editorSession: null,
    updateState: {},
    onSave: () => { },
    onRestore: () => { }
  });
  const paletteComponent = useSelector(state => _.get(state, `brandKit.components.paletteComponent.${id}.item`));

  const elementLoaded = _.get(paletteComponent, "id");
  const paletteComponentType = _.get(paletteComponent, "type");
  const loadingElement = useSelector(state => _.get(state, `pages.page.${id}.loading`, false));

  const editorSession = React.useRef(`editors_${id}`);

  const updateState = useSelector((state) => _.get(state, `brandKit.components.paletteComponent.update`));
  const restoreState = useSelector((state) => _.get(state, `brandKit.components.paletteComponent.restore`));
  const publishState = useSelector((state) => _.get(state, `brandKit.components.paletteComponent.publish`));

  const updating = _.get(updateState, 'updating');
  const publishing = _.get(publishState, 'publishing');

  useEffect(() => {
    var mixPanelPayload = getPageViewedEventPayload(MIXPANEL_PAGE_VIEWED_EDITOR)
    mixPanelPayload = {
      ...mixPanelPayload,
      editor_type: 'Brand kit',
      device_type: 'Desktop'
    }
    dispatch(createMixpanelEvent(MIXPANEL_PAGE_VIEWED, mixPanelPayload))
  }, []);

  useEffect(() => {
    dispatch(fetchPaletteComponent(id));
    dispatch(fetchIntegrations());
    editorSession.current = `editors_${id}`;
  }, [id]);

  useEffect(() => {
    if (editorSession.current === `editors_${id}`) {
      initialiseEditor();
      setHeaderContext(headerContext => {
        return {
          ...headerContext,
          entity: paletteComponent,
          editorSession: editorSession.current,
          updateState: updateState,
          onSave: handlePageSave,
          onRestore: handlePageRestore
        }
      })
    }
  }, [editorSession.current, elementLoaded]);

  useEffect(() => {
    if (paletteComponent) {
      setHeaderContext(headerContext => {
        return {
          ...headerContext,
          entity: paletteComponent,
        }
      })
    }
  }, [paletteComponent]);

  useEffect(() => {
    setHeaderContext(headerContext => {
      return {
        ...headerContext,
        updateState: updateState
      }
    })

    if (!updateState) {
      return;
    }

    if (updateState.updated) {
      toast(
        <SuccessToast
          heading={`Success`}
          description={`${renderToastName()} saved successfully`}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );

      dispatch(invalidatePaletteComponent(paletteComponent.id));
    } else if (updateState.error) {
      toast(
        <FailureToast
          heading={`Failure`}
          description={`Unable to save ${renderToastName()} palette component`}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );

      dispatch(invalidatePaletteComponent(paletteComponent.id));
    }
  }, [updateState]);

  useEffect(() => {
    setHeaderContext(headerContext => {
      return {
        ...headerContext,
        publishState: publishState
      }
    })

    if (!publishState) {
      return;
    }

    if (publishState.published) {
      toast(
        <SuccessToast
          heading={`Success`}
          description={`${renderToastName()} published successfully`}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );

      dispatch(invalidatePaletteComponent(paletteComponent.id));
    } else if (updateState.error) {
      toast(
        <FailureToast
          heading={`Failure`}
          description={`Unable to publish ${renderToastName()} palette component`}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );

      dispatch(invalidatePaletteComponent(paletteComponent.id));
    }
  }, [publishState]);

  const initialiseEditor = () => {
    const desktopGuides = _.get(paletteComponent, "draft.desktop.guides");
    const mobileGuides = _.get(paletteComponent, "draft.mobile.guides");
    let widgets = _.get(paletteComponent, "draft.widgets") || [];
    widgets = widgets.filter(item => _.get(item, 'type') !== "cleverstory.v2.widgets.palette.component");

    widgets = widgets.map(widget => ({
      ...widget,
      parent: widget.parent ==null ? 'widget_SfVs0utv' : widget.parent
    }));

    widgets = [PAGE_WIDGET, ...widgets];

    const fonts = _.get(paletteComponent, "draft.fonts") || [];

    const container = widgets.find(item => item.type === PAGE);

    const desktopHeight = parseInt(_.get(container, 'properties.desktop.layout.height'));
    const mobileHeight = parseInt(_.get(container, 'properties.desktop.layout.height'));

    _.set(container, 'properties.desktop.layout.width', '1200px');
    _.set(container, 'properties.desktop.layout.height', Math.max(600, desktopHeight));

    _.set(container, 'properties.mobile.layout.width', '375px');
    _.set(container, 'properties.mobile.layout.height', Math.max(600, mobileHeight));

    _.set(container, 'properties.desktop.container.minWidth', 1200);
    _.set(container, 'properties.mobile.container.minWidth', 375);

    const initialState = {
      desktop: {
        guides: desktopGuides
      },
      mobile: {
        guides: mobileGuides
      },
      widgets: widgets,
      entityType: 'PALETTE_COMPONENT',
      fonts: fonts,
      themes: _.get(paletteComponent, 'themes'),
      interactive: {
        active: _.get(container, 'uid')
      }
    }

    dispatch(createEditorSession(editorSession.current, initialState));
  }

  const handlePageSave = () => {
    dispatch(updatePaletteComponent(paletteComponent?.id, editorSession?.current));
  };

  const handlePageRestore = () => {

  }

  const renderToastName = () => {
    var name = _.get(paletteComponent, 'name');
    return name;
  }

  const renderPageState = () => {
    return "Loading Editor..."
  }

  const canPublish = () => {
    if (publishing || updating) {
      return false;
    }

    return _.get(paletteComponent, 'changes');
  }

  const handleRename = (name) => {
    dispatch(renamePaletteComponent(paletteComponent.id, name));
  }

  const handleSave = () => {
    dispatch(updatePaletteComponent(paletteComponent?.id, editorSession?.current));
  }

  const handlePublish = () => {
    dispatch(publishPaletteComponent(paletteComponent.id));

    const mixpanelPayload = {
      brandkit_element_type: formatString(paletteComponentType)
    }
      dispatch(createMixpanelEvent(MIXPANEL_BRANDKIT_PUBLISHED, mixpanelPayload));
  }

  const formatString = (value) => {
    if (value) {
      value = value.replace(/_/g, " ");

      value = value
        .toLowerCase()
        .split(" ")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
  
      if (!value.endsWith("s")) {
        value += "s";
      }
    }
    return value;
  };

  return (
    <div className={cx(styles.paletteComponent, styles.editor, { [styles.loadingElement]: loadingElement || !elementLoaded })}>
      <Loader className={cx(styles.elementLoader, { [styles.loadingPage]: loadingElement || !elementLoaded })} title={renderPageState()} />
      <header className={styles.header}>
        <div className={cx(styles.menu, styles.primary)}>
          <div className={styles.logoContainer}>
            <div className={styles.logo}></div>
          </div>
          <PaletteComponentNameField paletteComponent={paletteComponent} onSave={handleRename} />
          <div className={cx(styles.actions, styles.right)}>
            <Button
              variant="light"
              className={cx(styles.saveButton, styles.actionButton)}
              disabled={updating}
              onClick={handleSave}
            >
              {updating ? 'Saving...' : 'Save Changes'}
            </Button>

            <Button
              disabled={!canPublish()}
              variant="primary"
              onClick={handlePublish}>
              {publishing ? 'Saving...' : 'Save to Brand Kit'}
            </Button>
          </div>
        </div>
        <HeaderContext.Provider value={headerContext}>
          <PaletteComponentHeader editorSession={editorSession.current} target={{
            id: id,
            type: 'palette_component'
          }}></PaletteComponentHeader>
        </HeaderContext.Provider>
      </header>

      <Editor key={editorSession.current} id={editorSession.current} entityType='palette_component' />
    </div>
  );
}